import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileText } from "lucide-react";
import { SEO } from "../components/shared/SEO";
import { Reveal } from "../components/shared/Reveal";
import { CTABand } from "../components/shared/Sections";
import { Placeholder, StatusBadge } from "../components/operations/OperationCard";
import { FlowBand, Scenarios, MethodSteps, PhaseTimeline } from "../components/operations/OperationSections";
import { OperationMedia, InvestorForm } from "../components/operations/OperationMedia";
import { OP_STATUS } from "../components/operations/operationsData";
import { api, imgUrl, fmtEur, isPdf } from "../lib/api";

const SITE = process.env.REACT_APP_SITE_URL;

const Paras = ({ text, className = "lead mb-5", testId }) => (text || "").split("\n").filter(Boolean).map((p, i) => <p key={i} className={className} data-testid={testId ? `${testId}-${i}` : undefined}>{p}</p>);

const Fact = ({ label, value, testId }) => value ? (
  <div data-testid={testId} className="border-t border-ink/10 pt-6">
    <div className="label-line">{label}</div>
    <div className="font-serif text-2xl md:text-3xl font-light text-ink mt-2 leading-snug">{value}</div>
  </div>
) : null;

export default function OperazioneDetail() {
  const { slug } = useParams();
  const { data: o, isLoading, error } = useQuery({ queryKey: ["operation", slug], queryFn: () => api.get(`/operations/${slug}`).then((r) => r.data) });

  useEffect(() => {
    if (!o) return;
    const s = document.createElement("script"); s.type = "application/ld+json"; s.setAttribute("data-op-ld", "1");
    s.text = JSON.stringify({ "@context": "https://schema.org", "@type": "RealEstateListing", name: `${o.title} — ${o.location || o.city}`, description: o.summary, url: `${SITE}/operazioni/${o.slug}`, image: o.cover_image ? imgUrl(o.cover_image) : `${SITE}/og-image.png`, datePosted: o.created_at, about: { "@type": "Place", name: o.title, address: { "@type": "PostalAddress", streetAddress: o.title, addressLocality: o.city, addressCountry: "IT" } }, provider: { "@type": "Organization", name: "Home Haven Srl", url: `${SITE}/` } });
    document.head.appendChild(s);
    return () => s.remove();
  }, [o]);

  if (isLoading) return <div className="min-h-screen bg-ink" />;
  if (error || !o) return (
    <>
      <SEO title="Operazione non trovata" robots="noindex, follow" />
      <section className="min-h-[70vh] bg-ink text-cream flex items-center"><div className="container-hh pt-32"><h1 className="h-display">Operazione non trovata.</h1><Link to="/operazioni" className="btn-light mt-10">Tutte le operazioni</Link></div></section>
    </>
  );

  const scenarioMin = o.scenario_prudente?.value, scenarioMax = o.scenario_migliorativo?.value;

  return (
    <>
      <SEO title={o.seo_title || `${o.title}, ${o.city} — ${OP_STATUS[o.status]} | Home Haven`} description={o.seo_description || `${OP_STATUS[o.status]}. ${o.summary}`.slice(0, 160)} image={o.cover_image ? imgUrl(o.cover_image) : undefined} />

      {o.cover_image && o.cover_full_graphic ? (
        <section data-testid="op-hero">
          <div className="bg-ink pt-28 md:pt-36 pb-12 md:pb-16">
            <div className="container-hh">
              <nav aria-label="Breadcrumb" className="mb-6"><Link to="/operazioni" data-testid="op-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/70 hover:text-gold transition-colors"><ArrowLeft size={14} /> Operazioni aperte</Link></nav>
              <Reveal><img src={imgUrl(o.cover_image)} alt={`${o.title} — ${o.location || o.city}`} className="w-full h-auto shadow-2xl shadow-black/40" fetchPriority="high" data-testid="op-hero-graphic" /></Reveal>
            </div>
          </div>
          <div className="container-hh bg-cream">
            <Reveal delay={0.1} className="py-14 md:py-20 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
              <div>
                <StatusBadge status={o.status} />
                <h1 className="h-section mt-6 uppercase" data-testid="op-title">{o.title}</h1>
                <div className="eyebrow !text-stone mt-4" data-testid="op-location">{o.location || o.city}</div>
                {o.claim && <div className="mt-8 max-w-2xl"><Paras text={o.claim} className="font-serif text-xl md:text-2xl font-light tracking-tight leading-snug text-ink" testId="op-claim" /></div>}
              </div>
              <div className="flex flex-wrap gap-4 shrink-0">
                <a href="#dossier" data-testid="op-cta-dossier" className="btn-primary">Richiedi il dossier investitore</a>
                <a href="#progetto" data-testid="op-cta-progetto" className="btn-outline">Scopri il progetto</a>
              </div>
            </Reveal>
          </div>
        </section>
      ) : (
      <section className="relative min-h-[92vh] flex items-end bg-ink text-cream overflow-hidden" data-testid="op-hero">
        {o.cover_image ? <img src={imgUrl(o.cover_image)} alt={`${o.title} — ${o.location || o.city}`} className="absolute inset-0 img-cover opacity-60" fetchPriority="high" /> : <Placeholder dark text="" className="absolute inset-0" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="container-hh relative pb-20 md:pb-28 pt-48">
          <nav aria-label="Breadcrumb" className="mb-10"><Link to="/operazioni" data-testid="op-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/70 hover:text-gold transition-colors"><ArrowLeft size={14} /> Operazioni aperte</Link></nav>
          <Reveal>
            <StatusBadge status={o.status} light />
            <h1 className="h-display mt-8 uppercase" data-testid="op-title">{o.title}</h1>
            <div className="eyebrow !text-cream/70 mt-5" data-testid="op-location">{o.location || o.city}</div>
            {o.claim && <div className="mt-10 max-w-2xl"><Paras text={o.claim} className="font-serif text-2xl md:text-3xl lg:text-4xl font-light tracking-tight leading-snug text-cream" testId="op-claim" /></div>}
            <div className="flex flex-wrap gap-4 mt-12">
              <a href="#dossier" data-testid="op-cta-dossier" className="btn-light">Richiedi il dossier investitore</a>
              <a href="#progetto" data-testid="op-cta-progetto" className="btn-outline-light">Scopri il progetto</a>
            </div>
          </Reveal>
        </div>
      </section>
      )}

      <section id="progetto" className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4"><div className="eyebrow mb-5">Il progetto</div><h2 className="h-section">Da immobile da ristrutturare a prodotto finito.</h2></Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6"><Paras text={o.description} testId="op-description" /></Reveal>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-hh">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 mb-20 md:mb-28">
            <Reveal className="lg:col-span-4"><div className="eyebrow mb-5">Trasformazione dell'immobile</div><h2 className="h-section">Una completa reinterpretazione degli spazi.</h2></Reveal>
            <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6"><Paras text={o.transformation_text} testId="op-transformation" /></Reveal>
          </div>
          <FlowBand />
        </div>
      </section>

      <section className="section bg-beige/60">
        <div className="container-hh">
          <Reveal className="mb-14"><div className="eyebrow mb-5">Dati principali</div><h2 className="h-section">L'operazione in sintesi.</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8" data-testid="op-facts">
            <Fact label="Località" value={`${o.city} – ${o.title}`} testId="op-fact-location" />
            <Fact label="Tipologia" value={o.operation_type} testId="op-fact-type" />
            <Fact label="Prezzo target di acquisto" value={o.purchase_price != null ? fmtEur(o.purchase_price) : null} testId="op-fact-price" />
            <Fact label="Stato" value={OP_STATUS[o.status]} testId="op-fact-status" />
            <Fact label="Strategia" value={o.strategy} testId="op-fact-strategy" />
          </div>
          {scenarioMin != null && scenarioMax != null && <Reveal className="mt-12 text-sm font-light text-ink/60">Il prezzo indicato è l'obiettivo di acquisizione dell'operazione. Gli scenari di valorizzazione ({fmtEur(scenarioMin)} – {fmtEur(scenarioMax)}) sono ipotesi previsionali, non rendimenti garantiti.</Reveal>}
        </div>
      </section>

      <Scenarios o={o} />
      <MethodSteps />
      <OperationMedia o={o} />
      <PhaseTimeline phase={o.phase} />

      {o.documents?.length > 0 && (
        <section className="section bg-cream">
          <div className="container-hh">
            <Reveal className="mb-10"><div className="eyebrow mb-4">Documenti</div><h2 className="h-section">Materiali dell'operazione.</h2></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{o.documents.map((d, i) => <a key={d} href={imgUrl(d)} target="_blank" rel="noreferrer" data-testid={`op-doc-${i}`} className="flex items-center gap-4 border border-ink/10 bg-white p-8 hover:border-gold transition-colors"><FileText strokeWidth={1} size={32} /><span className="text-sm uppercase tracking-[0.18em]">{isPdf(d) ? `Documento ${i + 1} (PDF)` : `Documento ${i + 1}`}</span></a>)}</div>
          </div>
        </section>
      )}

      <InvestorForm o={o} />
      <CTABand eyebrow="Home Haven" title="Ogni operazione è un progetto completo." text="Scopri le operazioni concluse e il metodo con cui trasformiamo immobili da ristrutturare in prodotti finiti." primary={{ to: "/progetti", label: "Operazioni concluse", testId: "op-cta-progetti" }} secondary={{ to: "/contatti", label: "Contattaci", testId: "op-cta-contatti" }} />
    </>
  );
}
